import { z } from 'zod';
import { PET_MAX_EVOLUTION } from '../inventory/pets';
import { huntSessionStateSchema, progressionSchema } from './hunt';
import { activePetSchema, petsResponseSchema } from './pets';
import { decimalStringSchema, digestSchema } from './primitives';

/**
 * The two mutating pet routes (prompt 4A §3, §13).
 *
 * Separate from `pets.ts` because their answers carry a hunt session, and the
 * hunt's own contract reports pet state from `pets.ts` - keeping this at the
 * end of the chain is what stops the two from importing each other, which with
 * zod schemas evaluated at module load leaves one of them undefined.
 *
 * Both answers describe the SAME session, reconfigured in place. Neither
 * clears the field, restarts the boss, discards a projectile or opens a new
 * run: activating a pet and evolving one are changes to what the ship fights
 * alongside, not to the fight.
 */

export const PET_ACTIVATE_ROUTE = '/api/pets/activate' as const;
export const PET_EVOLVE_ROUTE = '/api/pets/evolve' as const;

/**
 * Activate one pet, or deactivate whatever is active.
 *
 * `petInstanceId: null` deactivates - an explicit value rather than an omitted
 * field, so `.strict()` can still reject everything else and the intent is
 * never inferred from an absence.
 *
 * The body cannot state a definition, an evolution, a rarity or a star count:
 * all four are read from the row under its lock. A client that lies is naming
 * an instance it does not own.
 */
export const petActivateRequestSchema = z
  .object({
    petInstanceId: z.uuid().nullable(),
    /**
     * The loadout version the client believes it is changing.
     *
     * Two tabs that both activate from the same view carry the same number and
     * only the first can win; the loser is told to refresh rather than
     * silently overwriting a choice the player made elsewhere.
     */
    expectedLoadoutVersion: z.number().int().min(0),
    /** Replay guard for THIS request. Never used as a ledger key. */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type PetActivateRequest = z.infer<typeof petActivateRequestSchema>;

/** Everything the scene needs to keep playing after the pet changed. */
export const petActivateResponseSchema = z.object({
  /** The pet flying now, or null after a deactivation. */
  activePet: activePetSchema.nullable(),
  /** The complete pet state afterwards, re-derived from the vault. */
  pets: petsResponseSchema,
  /**
   * The SAME session, reconfigured in place. The arena is never recreated:
   * mobs, boss, timers, the spawn schedule and the shots in flight continue.
   */
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  progression: progressionSchema,
  /** What the pending tail earned under the OLD pet, before the change. */
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
  /** True when the same key was already applied; nothing changed again. */
  replayed: z.boolean(),
});
export type PetActivateResponse = z.infer<typeof petActivateResponseSchema>;

/**
 * Evolve two copies of the same pet at the same evolution into the next form.
 *
 * The same request shape as merging two artifacts, and the same rules: two
 * instance ids and a replay key, nothing else. Evolving is MANUAL - nothing
 * merges duplicates on the player's behalf.
 */
export const petEvolveRequestSchema = z
  .object({
    firstInstanceId: z.uuid(),
    secondInstanceId: z.uuid(),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type PetEvolveRequest = z.infer<typeof petEvolveRequestSchema>;

export const petEvolveResponseSchema = z.object({
  evolved: z.object({
    definitionId: z.string().min(1),
    consumedInstanceIds: z.tuple([z.uuid(), z.uuid()]),
    newInstanceId: z.uuid(),
    fromEvolution: z.number().int().min(1),
    toEvolution: z.number().int().min(2).max(PET_MAX_EVOLUTION),
    /**
     * True when the pet that evolved was ACTIVE, and the new form took over
     * the slot without a gap. There is no moment in which the account has no
     * pet, and nothing for the running hunt to notice beyond stronger guns.
     */
    stayedActive: z.boolean(),
  }),
  activePet: activePetSchema.nullable(),
  pets: petsResponseSchema,
  session: huntSessionStateSchema,
  snapshot: z.unknown(),
  snapshotDigest: digestSchema,
  progression: progressionSchema,
  settled: z.object({
    validatedMs: z.number().int().min(0),
    xpEarned: decimalStringSchema,
    goldEarned: decimalStringSchema,
    levelsGained: decimalStringSchema,
  }),
  replayed: z.boolean(),
});
export type PetEvolveResponse = z.infer<typeof petEvolveResponseSchema>;
